import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import {
  collection, query, where, orderBy, onSnapshot, doc, getDoc, addDoc, deleteDoc
} from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function ClubDashboard() {
  const { logout, currentUser, userClubId } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sessions, setSessions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [clubName, setClubName] = useState('');
  const [clubLogoUrl, setClubLogoUrl] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerGroup, setNewPlayerGroup] = useState('A');
  const [addingPlayer, setAddingPlayer] = useState(false);

  const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  useEffect(() => {
    if (!userClubId) return;

    // Get club name
    getDoc(doc(db, 'clubs', userClubId)).then(d => {
      if (d.exists()) {
        setClubName(d.data().name);
        setClubLogoUrl(d.data().logoUrl || '');
      }
    });

    // Load players
    const unsubPlayers = onSnapshot(
      collection(db, 'clubs', userClubId, 'players'),
      snap => setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Load sessions
    const unsubSessions = onSnapshot(
      query(
        collection(db, 'sessions'),
        where('clubId', '==', userClubId),
        orderBy('timestamp', 'desc')
      ),
      snap => setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Load attendance
    const unsubAttendance = onSnapshot(
      query(collection(db, 'attendance'), where('clubId', '==', userClubId)),
      snap => setAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    return () => { unsubPlayers(); unsubSessions(); unsubAttendance(); };
  }, [userClubId]);

  // Calculate ACWR for a player. Matches by playerId (added to sessions
  // once players started being selected by ID instead of name), falling
  // back to playerName for older sessions logged before that change.
  function calcACWR(player) {
    const playerSessions = sessions.filter(s =>
      s.playerId ? s.playerId === player.id : s.playerName === player.name
    );
    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const fourWeeksAgo = new Date(now - 28 * 24 * 60 * 60 * 1000);

    const acute = playerSessions
      .filter(s => new Date(s.timestamp?.toDate?.() || s.timestamp) >= oneWeekAgo)
      .reduce((sum, s) => sum + (s.load || 0), 0);

    const chronic = playerSessions
      .filter(s => new Date(s.timestamp?.toDate?.() || s.timestamp) >= fourWeeksAgo)
      .reduce((sum, s) => sum + (s.load || 0), 0) / 4;

    return chronic > 0 ? parseFloat((acute / chronic).toFixed(2)) : null;
  }

  function getACWRBadge(acwr) {
    if (!acwr) return <span style={{ color: '#999' }}>—</span>;
    if (acwr < 0.8) return <span className="badge badge-purple">Unterforderung ({acwr})</span>;
    if (acwr <= 1.3) return <span className="badge badge-green">Sweet Spot ({acwr})</span>;
    if (acwr <= 1.49) return <span className="badge badge-yellow">Erhöht ({acwr})</span>;
    return <span className="badge badge-red">⚠️ Gefahr ({acwr})</span>;
  }

  function getLoadBadge(load) {
    if (!load) return <span style={{ color: '#999' }}>—</span>;
    if (load < 500) return <span className="badge badge-purple">{load}</span>;
    if (load < 700) return <span className="badge badge-green">{load}</span>;
    if (load <= 900) return <span className="badge badge-yellow">{load}</span>;
    return <span className="badge badge-red">{load}</span>;
  }

  // Weekly load per player for chart
  function getPlayerWeeklyData(player) {
    const playerSessions = sessions.filter(s =>
      s.playerId ? s.playerId === player.id : s.playerName === player.name
    );
    const weeks = {};
    playerSessions.forEach(s => {
      const w = s.week || 'Unbekannt';
      if (!weeks[w]) weeks[w] = 0;
      weeks[w] += s.load || 0;
    });
    return Object.entries(weeks).map(([week, total]) => ({ week, total })).slice(-8);
  }

  // Monthly overview
  function getMonthlyLoad(player, monthIdx) {
    return sessions
      .filter(s => {
        const d = s.timestamp?.toDate?.() || new Date(s.timestamp);
        const matches = s.playerId ? s.playerId === player.id : s.playerName === player.name;
        return matches && d.getMonth() === monthIdx;
      })
      .reduce((sum, s) => sum + (s.load || 0), 0);
  }

  // Attendance
  function getAttendanceCount(playerId) {
    return attendance.filter(a => a.playerId === playerId && a.month === selectedMonth).length;
  }

  async function handleAddPlayer() {
    if (!newPlayerName.trim()) {
      alert('Bitte einen Namen eingeben.');
      return;
    }
    setAddingPlayer(true);
    try {
      await addDoc(collection(db, 'clubs', userClubId, 'players'), {
        name: newPlayerName.trim(),
        group: newPlayerGroup
      });
      setNewPlayerName('');
      setNewPlayerGroup('A');
    } catch (err) {
      alert('Fehler beim Hinzufügen. Bitte nochmal versuchen.');
    }
    setAddingPlayer(false);
  }

  async function handleRemovePlayer(playerId) {
    if (!window.confirm('Diesen Spieler wirklich entfernen?')) return;
    try {
      await deleteDoc(doc(db, 'clubs', userClubId, 'players', playerId));
    } catch (err) {
      alert('Fehler beim Entfernen. Bitte nochmal versuchen.');
    }
  }

  const daysInMonth = new Date(new Date().getFullYear(), selectedMonth + 1, 0).getDate();

  // Stats
  const thisWeekSessions = sessions.filter(s => {
    const d = s.timestamp?.toDate?.() || new Date(s.timestamp);
    return (new Date() - d) < 7 * 24 * 60 * 60 * 1000;
  });
  const criticalSessions = sessions.filter(s => s.load > 900);
  const dangerPlayers = players.filter(p => {
    const acwr = calcACWR(p);
    return acwr && acwr >= 1.5;
  });

  return (
    <div className="app-container">
      {/* Navbar */}
      <div className="navbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {clubLogoUrl && (
            <img
              src={clubLogoUrl}
              alt={clubName}
              style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6 }}
            />
          )}
          <div>
            <div className="navbar-title">RPE System — {clubName}</div>
            <div className="navbar-subtitle">Belastungsmonitoring</div>
          </div>
        </div>
        <div className="navbar-right">
          <span style={{ fontSize: 13, color: '#888' }}>{currentUser?.email}</span>
          <button className="btn-logout" onClick={logout}>Abmelden</button>
        </div>
      </div>

      <div className="page-content">
        {/* Stats */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-number">{players.length}</div>
            <div className="stat-label">Spieler</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{thisWeekSessions.length}</div>
            <div className="stat-label">Einheiten diese Woche</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{criticalSessions.length}</div>
            <div className="stat-label">🔴 Kritische Einheiten</div>
          </div>
          <div className="stat-card">
            <div className="stat-number" style={{ color: dangerPlayers.length > 0 ? '#C0392B' : '#1E8449' }}>
              {dangerPlayers.length}
            </div>
            <div className="stat-label">⚠️ Spieler in Gefahr-Zone</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          {['overview', 'players', 'weekly', 'monthly', 'sessions', 'attendance'].map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' && '📊 ACWR Übersicht'}
              {tab === 'players' && '👥 Spieler'}
              {tab === 'weekly' && '📈 Wochenübersicht'}
              {tab === 'monthly' && '📅 Monatsübersicht'}
              {tab === 'sessions' && '📋 Einheiten'}
              {tab === 'attendance' && '✅ Anwesenheit'}
            </button>
          ))}
        </div>

        {/* ACWR OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="card">
            <div className="card-title">ACWR — Verletzungsrisiko alle Spieler</div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="sticky-col">Spieler</th>
                    <th>Gruppe</th>
                    <th>ACWR (4 Wochen)</th>
                    <th>Risikozone</th>
                    <th>Akute Belastung (7 Tage)</th>
                    <th>Letzte Einheit</th>
                    <th>Wohlbefinden</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => {
                    const acwr = calcACWR(player);
                    const playerSessions = sessions.filter(s =>
                      s.playerId ? s.playerId === player.id : s.playerName === player.name
                    );
                    const lastSession = playerSessions[0];
                    const now = new Date();
                    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
                    const acuteLoad = playerSessions
                      .filter(s => new Date(s.timestamp?.toDate?.() || s.timestamp) >= oneWeekAgo)
                      .reduce((sum, s) => sum + (s.load || 0), 0);

                    return (
                      <tr key={player.id}>
                        <td className="sticky-col" style={{ textAlign: 'left', fontWeight: 'bold' }}>{player.name}</td>
                        <td>{player.group}</td>
                        <td>{getACWRBadge(acwr)}</td>
                        <td style={{ fontSize: 12 }}>
                          {!acwr ? '—' :
                            acwr < 0.8 ? 'Unterforderung' :
                            acwr <= 1.3 ? 'Sweet Spot' :
                            acwr <= 1.49 ? 'Erhöhte Belastung' : 'GEFAHR'}
                        </td>
                        <td>{acuteLoad > 0 ? getLoadBadge(acuteLoad) : '—'}</td>
                        <td style={{ fontSize: 12 }}>
                          {lastSession ? `${lastSession.sessionType}` : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>{lastSession?.wellness || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* WEEKLY TAB */}
        {activeTab === 'weekly' && (
          <div>
            <div className="card">
              <div className="card-title">Wochenbelastung — Alle Spieler</div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th className="sticky-col">Spieler</th>
                      <th>Gruppe</th>
                      {[...new Set(sessions.map(s => s.week))].sort().slice(-8).map(w => (
                        <th key={w}>{w}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(player => {
                      const weeks = [...new Set(sessions.map(s => s.week))].sort().slice(-8);
                      return (
                        <tr key={player.id}>
                          <td className="sticky-col" style={{ textAlign: 'left', fontWeight: 'bold' }}>{player.name}</td>
                          <td>{player.group}</td>
                          {weeks.map(w => {
                            const load = sessions
                              .filter(s => (s.playerId ? s.playerId === player.id : s.playerName === player.name) && s.week === w)
                              .reduce((sum, s) => sum + (s.load || 0), 0);
                            return <td key={w}>{load > 0 ? getLoadBadge(load) : '—'}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Chart */}
            <div className="card">
              <div className="card-title">Teambelastung pro Woche</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={(() => {
                  const weeks = [...new Set(sessions.map(s => s.week))].sort().slice(-8);
                  return weeks.map(w => ({
                    week: w,
                    total: sessions.filter(s => s.week === w).reduce((sum, s) => sum + (s.load || 0), 0)
                  }));
                })()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#C0392B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* PLAYERS TAB */}
        {activeTab === 'players' && (
          <div className="card">
            <div className="card-title">Spieler hinzufügen — {clubName}</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newPlayerName}
                onChange={e => setNewPlayerName(e.target.value)}
                placeholder="Spielername"
                style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #DDD', fontSize: 16, flex: 1, minWidth: 180 }}
              />
              <select
                value={newPlayerGroup}
                onChange={e => setNewPlayerGroup(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #DDD', fontSize: 16 }}
              >
                {['A', 'B', 'C'].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <button className="btn-primary" onClick={handleAddPlayer} disabled={addingPlayer}>
                {addingPlayer ? 'Wird hinzugefügt...' : '+ Hinzufügen'}
              </button>
            </div>

            <div className="card-title">Spieler — {clubName} ({players.length} Spieler)</div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Gruppe</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => (
                    <tr key={player.id}>
                      <td style={{ textAlign: 'left', fontWeight: 'bold' }}>{player.name}</td>
                      <td>{player.group}</td>
                      <td>
                        <button
                          onClick={() => handleRemovePlayer(player.id)}
                          style={{
                            padding: '6px 14px',
                            border: '1px solid #C0392B',
                            borderRadius: 6,
                            background: '#FFF',
                            color: '#C0392B',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 'bold'
                          }}
                        >
                          Entfernen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MONTHLY TAB */}
        {activeTab === 'monthly' && (
          <div className="card">
            <div className="card-title">Monatsübersicht — Belastung & ACWR</div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="sticky-col">Spieler</th>
                    <th>Gr.</th>
                    {months.map(m => <th key={m}>{m}</th>)}
                    <th>Akute</th>
                    <th>Chronische</th>
                    <th>ACWR</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => {
                    const monthLoads = months.map((_, i) => getMonthlyLoad(player, i));
                    const nonZero = monthLoads.filter(l => l > 0);
                    const akute = nonZero.length > 0 ? nonZero[nonZero.length - 1] : 0;
                    const chronic = nonZero.length > 0 ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 0;
                    const acwr = chronic > 0 ? parseFloat((akute / chronic).toFixed(2)) : null;
                    return (
                      <tr key={player.id}>
                        <td className="sticky-col" style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 12 }}>{player.name}</td>
                        <td>{player.group}</td>
                        {monthLoads.map((load, i) => (
                          <td key={i}>{load > 0 ? <span style={{ fontSize: 12 }}>{load.toLocaleString()}</span> : <span style={{ color: '#CCC' }}>—</span>}</td>
                        ))}
                        <td style={{ fontWeight: 'bold' }}>{akute > 0 ? akute.toLocaleString() : '—'}</td>
                        <td style={{ fontSize: 12 }}>{chronic > 0 ? chronic.toFixed(0) : '—'}</td>
                        <td>{getACWRBadge(acwr)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SESSIONS TAB */}
        {activeTab === 'sessions' && (
          <div className="card">
            <div className="card-title">Einheiten — {clubName}</div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th className="sticky-col">Spieler</th>
                    <th>Art</th>
                    <th>Dauer</th>
                    <th>RPE</th>
                    <th>Belastung (AU)</th>
                    <th>Wohlbefinden</th>
                    <th>Woche</th>
                    <th>Tag</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.slice(0, 100).map(s => {
                    const d = s.timestamp?.toDate?.() || new Date(s.timestamp);
                    return (
                      <tr key={s.id}>
                        <td style={{ fontSize: 12 }}>{d.toLocaleDateString('de-AT')}</td>
                        <td className="sticky-col" style={{ textAlign: 'left', fontWeight: 'bold' }}>{s.playerName}</td>
                        <td>{s.sessionType}</td>
                        <td>{s.duration} Min</td>
                        <td>{s.rpe}</td>
                        <td>{getLoadBadge(s.load)}</td>
                        <td style={{ fontSize: 12 }}>{s.wellness}</td>
                        <td style={{ fontSize: 12 }}>{s.week}</td>
                        <td style={{ fontSize: 12 }}>{s.day}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ATTENDANCE TAB */}
        {activeTab === 'attendance' && (
          <div className="card">
            <div className="card-title">Anwesenheitsliste — {months[selectedMonth]}</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 'bold', marginRight: 8 }}>Monat:</label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(parseInt(e.target.value))}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #DDD', fontSize: 16 }}
              >
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="sticky-col" style={{ textAlign: 'left', minWidth: 140 }}>Spieler</th>
                    {Array.from({ length: daysInMonth }, (_, i) => (
                      <th key={i} style={{ minWidth: 28, fontSize: 11 }}>{i + 1}</th>
                    ))}
                    <th>∑</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => {
                    const count = getAttendanceCount(player.id);
                    return (
                      <tr key={player.id}>
                        <td className="sticky-col" style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 12 }}>{player.name}</td>
                        {Array.from({ length: daysInMonth }, (_, i) => {
                          const present = attendance.some(
                            a => a.playerId === player.id && a.month === selectedMonth && a.day === i + 1
                          );
                          return (
                            <td key={i} style={{ padding: 2 }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: 4, margin: '0 auto',
                                background: present ? '#C0392B' : '#EEE',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, color: present ? '#FFF' : '#CCC', fontWeight: 'bold'
                              }}>
                                {present ? '✓' : ''}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ fontWeight: 'bold' }}>{count}</td>
                        <td style={{ fontSize: 12, color: count / daysInMonth > 0.7 ? '#1E8449' : '#C0392B' }}>
                          {Math.round((count / daysInMonth) * 100)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
              * Anwesenheit wird automatisch erfasst, sobald ein Spieler das RPE-Formular für eine Einheit ausfüllt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}